import React, { useState, useRef } from 'react';
import { uploadFile } from '@/utils/upload';

export interface UploadedAttachment {
  id: string;
  file_name: string;
  file_size?: number;
  url?: string;
}

interface TicketAttachmentUploaderProps {
  value?: UploadedAttachment[];
  onChange?: (attachments: UploadedAttachment[]) => void;
  maxCount?: number;
  disabled?: boolean;
}

export function TicketAttachmentUploader({
  value = [],
  onChange,
  maxCount = 5,
  disabled = false,
}: TicketAttachmentUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (value.length + files.length > maxCount) {
      setUploadError(`最多可上传 ${maxCount} 个附件`);
      return;
    }

    setUploading(true);
    setUploadError(null);

    const newAttachments: UploadedAttachment[] = [...value];

    try {
      for (const file of files) {
        // 使用 SUPPORT_TICKET 业务场景与 parent-visible 策略
        const res = await uploadFile(file, 'SUPPORT_TICKET', 'parent-visible');
        const assetId = res.asset_id;
        if (assetId) {
          newAttachments.push({
            id: assetId,
            file_name: res.file_name || file.name,
            file_size: res.file_size || file.size,
            url: res.file_url,
          });
        }
      }
      onChange?.(newAttachments);
    } catch (err: any) {
      console.error('上传附件失败', err);
      setUploadError(err?.message || '附件上传失败，请重试');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = (index: number) => {
    const next = [...value];
    next.splice(index, 1);
    onChange?.(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-semibold text-slate-700">
          问题相关附件 <span className="text-slate-400 font-normal">（选填，最多 {maxCount} 个）</span>
        </label>
        {value.length < maxCount && (
          <button
            type="button"
            disabled={uploading || disabled}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer disabled:opacity-50"
          >
            <i className="ri-attachment-2"></i>
            {uploading ? '上传中...' : '添加附件'}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
      />

      {uploadError && (
        <p className="text-xs text-rose-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i> {uploadError}
        </p>
      )}

      {/* 已上传的附件列表 */}
      {value.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          {value.map((item, idx) => (
            <div
              key={item.id + idx}
              className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-200 text-xs group"
            >
              <div className="flex items-center gap-2 min-w-0 pr-2">
                <i className="ri-file-text-line text-slate-400 text-sm flex-shrink-0"></i>
                <span className="truncate text-slate-700 font-medium" title={item.file_name}>
                  {item.file_name}
                </span>
                {item.file_size ? (
                  <span className="text-slate-400 text-[10px] flex-shrink-0">({formatFileSize(item.file_size)})</span>
                ) : null}
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  className="text-slate-400 hover:text-rose-500 transition-colors p-0.5"
                  title="删除附件"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
